import React, { useState, useEffect, useContext } from 'react';
import { 
  Pill, Droplet, Syringe, Wine, CheckCircle, AlertTriangle, Clock,
  Thermometer, Shield, MapPin, Filter, Search, User, Eye, X
} from 'lucide-react';
import '../../assets/css/receiverdashboard/MedicineDonation.css';
import ReceiverMasterLayout from '../../components/layout/ReceiverMasterLayout';
import AppContext from '../../context/AppContext';
import axios from 'axios';
import Swal from 'sweetalert2';

const MedicineDonation = () => {
  const { baseUrl } = useContext(AppContext);

  const [medicinesData, setMedicinesData]     = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [filterCategory, setFilterCategory]   = useState('all');
  const [searchQuery, setSearchQuery]         = useState('');
  const [claimLoading, setClaimLoading]       = useState(false);

  // ========================= FETCH DATA =========================
  useEffect(() => {
    fetchMedicines();
  }, []);

  const fetchMedicines = () => {
    axios.get(`${baseUrl}/donor/dashboard/viewAllMedicine`)
      .then((res) => {
        if (res.data.success) setMedicinesData(res.data.donations);
      })
      .catch((err) => console.log(err));
  };

  // ========================= CLAIM =========================
  const handleClaim = (medicine, e) => {
    e.stopPropagation();

    if (medicine.donation === 'collected') {
      Swal.fire({ title: "Already Claimed!", text: "This medicine has already been claimed.", icon: "info" });
      return;
    }

    Swal.fire({
      title: "Claim Medicine?",
      text: `Are you sure you want to claim ${medicine.medicine_name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Claim it!",
      confirmButtonColor: "#1F7A7A",
    }).then((result) => {
      if (result.isConfirmed) {
        setClaimLoading(true);
        const formData = new FormData();
        formData.append("id", medicine.id);

        axios.post(`${baseUrl}/receiver/medicine/claimMedicine`, formData)
          .then((res) => {
            if (res.data.success) {
              Swal.fire({ title: "Claimed!", text: "Medicine claimed successfully.", icon: "success" });
              fetchMedicines();
              // Modal mein bhi update karo
              if (selectedMedicine && selectedMedicine.id === medicine.id) {
                setSelectedMedicine({ ...selectedMedicine, donation: 'collected' });
              }
            } else {
              Swal.fire({ title: "Oops!", text: res.data.message || "Cannot claim medicine.", icon: "error" });
            }
          })
          .catch((err) => console.log(err))
          .finally(() => setClaimLoading(false));
      }
    });
  };

  // Categories
  const categories = [
    { id: 'all',              name: 'All Medicines',    icon: Pill,    color: '#1F7A7A' },
    { id: 'general_medicine', name: 'General Medicine', icon: Pill,    color: '#FFB800' },
    { id: 'chronic_disease',  name: 'Chronic Disease',  icon: Pill,    color: '#FF6B4A' },
    { id: 'pediatric',        name: 'Pediatric',        icon: Pill,    color: '#52C41A' },
    { id: 'antibiotics',      name: 'Antibiotics',      icon: Syringe, color: '#00BCD4' },
    { id: 'vitamins',         name: 'Vitamins',         icon: Wine,    color: '#9C27B0' },
    { id: 'surgical',         name: 'Surgical',         icon: Droplet, color: '#DC2626' },
  ];

  // ========================= HELPERS =========================
  const getExpiryDays = (expiryDate) => {
    if (!expiryDate) return 0;
    const diff = new Date(expiryDate) - new Date();
    return Math.max(Math.floor(diff / (1000 * 60 * 60 * 24)), 0);
  };

  const getExpiryStatus = (days) => {
    if (days > 365) return { status: 'safe',     color: '#52C41A', label: 'Safe'     };
    if (days > 90)  return { status: 'warning',  color: '#FFB800', label: 'Monitor'  };
    if (days > 30)  return { status: 'urgent',   color: '#FF6B4A', label: 'Urgent'   };
    return               { status: 'critical', color: '#DC2626', label: 'Critical' };
  };

  const getExpiryPercentage = (days) => Math.min((days / 730) * 100, 100);

  const getCategoryIcon  = (category) => { const cat = categories.find(c => c.id === category); return cat ? cat.icon  : Pill;      };
  const getCategoryColor = (category) => { const cat = categories.find(c => c.id === category); return cat ? cat.color : '#1F7A7A'; };
  const getCategoryLabel = (category) => { const cat = categories.find(c => c.id === category); return cat ? cat.name  : category;  };

  const getStatusBadge = (status) => {
    const map = {
      pending:   { label: 'Pending',   color: '#FFB800' },
      verified:  { label: 'Verified',  color: '#52C41A' },
      collected: { label: 'Claimed',   color: '#1F7A7A' },
      cancelled: { label: 'Cancelled', color: '#DC2626' },
    };
    return map[(status || 'pending').toLowerCase()] || map['pending'];
  };

  // ========================= FILTER =========================
  const filteredMedicines = medicinesData.filter(med => {
    const matchesCategory = filterCategory === 'all' || med.medicine_category === filterCategory;
    const matchesSearch   =
      (med.medicine_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (med.donor_name    || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // ========================= STATS =========================
  const stats = {
    total:          medicinesData.length,
    verified:       medicinesData.filter(m => (m.donation || '') === 'verified').length,
    sealed:         medicinesData.filter(m => m.is_unopened == 1).length,
    needsAttention: medicinesData.filter(m => getExpiryDays(m.expiry_date) < 90).length,
  };

  return (
    <ReceiverMasterLayout>
      <div className="medicine-dashboard">

        {/* Header */}
        <div className="medicine-header">
          <div>
            <h1 className="medi-page-title">Medicine Dashboard</h1>
            <p className="medi-page-subtitle">Pharmacy-Grade Medicine Tracking & Management</p>
          </div>
        </div>

        {/* Stats */}
        <div className="medicine-stats">
          <div className="stat-card stat-teal">
            <div className="stat-icon"><Pill size={28} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Medicines</span>
            </div>
          </div>
          <div className="stat-card stat-green">
            <div className="stat-icon"><CheckCircle size={28} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.verified}</span>
              <span className="stat-label">Verified Items</span>
            </div>
          </div>
          <div className="stat-card stat-golden">
            <div className="stat-icon"><Shield size={28} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.sealed}</span>
              <span className="stat-label">Sealed Packages</span>
            </div>
          </div>
          <div className="stat-card stat-red">
            <div className="stat-icon"><AlertTriangle size={28} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.needsAttention}</span>
              <span className="stat-label">Needs Attention</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="medicine-filters">
          <div className="category-tabs">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  className={`category-tab ${filterCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setFilterCategory(cat.id)}
                  style={{
                    borderColor: filterCategory === cat.id ? cat.color : 'transparent',
                    color:       filterCategory === cat.id ? cat.color : '#a0b9b9',
                  }}
                >
                  <Icon size={20} />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
          <div className="search-filters">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search by medicine or donor name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="filter-btn">
              <Filter size={18} /> Advanced Filter
            </button>
          </div>
        </div>

        {/* Medicine Grid */}
        <div className="medicine-grid">
          {filteredMedicines.length === 0 ? (
            <div className="medicine-empty-state w-100">
              <h4>No Medicines Found</h4>
              <p>Try adjusting your filters</p>
            </div>
          ) : (
            filteredMedicines.map((medicine) => {
              const Icon         = getCategoryIcon(medicine.medicine_category);
              const catColor     = getCategoryColor(medicine.medicine_category);
              const expiryDays   = getExpiryDays(medicine.expiry_date);
              const expiryStatus = getExpiryStatus(expiryDays);
              const expiryPct    = getExpiryPercentage(expiryDays);
              const donStatus    = getStatusBadge(medicine.donation);
              const isClaimed    = medicine.donation === 'collected';

              return (
                <div key={medicine.id} className="medicine-card" onClick={() => setSelectedMedicine(medicine)}>

                  {/* Card Header */}
                  <div className="card-header">
                    <div className="medicine-icon" style={{ background: `${catColor}20` }}>
                      <Icon size={24} style={{ color: catColor }} />
                    </div>
                    <div className="card-badges">
                      {medicine.is_unopened == 1 && (
                        <span className="badge sealed-badge">
                          <Shield size={14} /> Sealed
                        </span>
                      )}
                      <span className="badge" style={{ background: donStatus.color, color: '#fff', fontSize: '11px' }}>
                        {donStatus.label}
                      </span>
                    </div>
                  </div>

                  {/* Medicine Info */}
                  <div className="medicine-info">
                    <h3 className="medicine-brand">{medicine.medicine_name}</h3>
                    <p className="medicine-generic text-capitalize">{getCategoryLabel(medicine.medicine_category)}</p>
                    <p className="medicine-batch">Donor Type: <span className="text-capitalize">{medicine.donor_type}</span></p>
                  </div>

                  {/* Quantity */}
                  <div className="medicine-quantity">
                    <span className="quantity-value">{medicine.quantity}</span>
                    <span className="quantity-unit text-capitalize">{medicine.quantity_unit}</span>
                  </div>

                  {/* Expiry Tracker */}
                  <div className="expiry-tracker">
                    <div className="expiry-header">
                      <Clock size={14} />
                      <span className="expiry-label">Expires: {medicine.expiry_date}</span>
                      <span className="expiry-status" style={{ color: expiryStatus.color }}>
                        {expiryStatus.label}
                      </span>
                    </div>
                    <div className="expiry-progress-bar">
                      <div
                        className={`expiry-progress ${expiryStatus.status}`}
                        style={{ width: `${expiryPct}%`, background: expiryStatus.color }}
                      />
                    </div>
                  </div>

                  {/* Storage */}
                  <div className="storage-info">
                    <Thermometer size={16} />
                    <span>Pickup: {medicine.pickup_date}</span>
                  </div>

                  {/* Donor Info */}
                  <div className="donor-info">
                    <div className="donor-name"><User size={14} /> {medicine.donor_name}</div>
                    <div className="donor-location"><MapPin size={14} /> {medicine.city}</div>
                  </div>

                  {/* Footer */}
                  <div className="card-footer" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="view-details-btn"
                      onClick={(e) => { e.stopPropagation(); setSelectedMedicine(medicine); }}
                    >
                      <Eye size={16} /> View Details
                    </button>
                    <button
                      className="view-details-btn"
                      disabled={isClaimed || claimLoading}
                      onClick={(e) => handleClaim(medicine, e)}
                      style={{
                        background:  isClaimed ? '#a0a0a0' : '#1F7A7A',
                        cursor:      isClaimed ? 'not-allowed' : 'pointer',
                        opacity:     isClaimed ? 0.7 : 1,
                      }}
                    >
                      <CheckCircle size={16} />
                      {isClaimed ? 'Claimed' : 'Claim'}
                    </button>
                  </div>

                </div>
              );
            })
          )}
        </div>

        {/* Detail Modal */}
        {selectedMedicine && (
          <div className="medicine-modal-overlay" onClick={() => setSelectedMedicine(null)}>
            <div className="medicine-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedMedicine(null)}>
                <X size={24} />
              </button>

              <div className="modal-header">
                <h2>{selectedMedicine.medicine_name}</h2>
                <p className="text-capitalize">{getCategoryLabel(selectedMedicine.medicine_category)}</p>
              </div>

              <div className="modal-body">
                <p><strong>Donor Name:</strong> {selectedMedicine.donor_name}</p>
                <p><strong>Donor Type:</strong> <span className="text-capitalize">{selectedMedicine.donor_type}</span></p>
                <p><strong>Quantity:</strong> {selectedMedicine.quantity} {selectedMedicine.quantity_unit}</p>
                <p><strong>Expiry Date:</strong> {selectedMedicine.expiry_date}</p>
                <p><strong>Pickup Date:</strong> {selectedMedicine.pickup_date}</p>
                <p><strong>Available Time:</strong> <span className="text-capitalize">{selectedMedicine.available_time}</span></p>
                <p><strong>City:</strong> {selectedMedicine.city}</p>
                <p><strong>Pickup Address:</strong> {selectedMedicine.pickup_address}</p>
                <p><strong>Contact:</strong> {selectedMedicine.contact_number}</p>
                <p><strong>Description:</strong> {selectedMedicine.description}</p>
                <p><strong>Sealed/Unopened:</strong> {selectedMedicine.is_unopened == 1 ? 'Yes' : 'No'}</p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    style={{
                      color: getStatusBadge(selectedMedicine.donation).color,
                      fontWeight: 'bold',
                      textTransform: 'capitalize',
                    }}
                  >
                    {getStatusBadge(selectedMedicine.donation).label}
                  </span>
                </p>
                <hr />
                {/* Claim Button in Modal */}
                <button
                  className="view-details-btn w-100 mt-2"
                  disabled={selectedMedicine.donation === 'collected' || claimLoading}
                  onClick={(e) => handleClaim(selectedMedicine, e)}
                  style={{
                    background: selectedMedicine.donation === 'collected' ? '#a0a0a0' : '#1F7A7A',
                    cursor:     selectedMedicine.donation === 'collected' ? 'not-allowed' : 'pointer',
                    opacity:    selectedMedicine.donation === 'collected' ? 0.7 : 1,
                    width:      '100%',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle size={16} />
                  {selectedMedicine.donation === 'collected' ? 'Already Claimed' : 'Claim This Medicine'}
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </ReceiverMasterLayout>
  );
};

export default MedicineDonation;